import {
  format,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
  getISOWeek,
} from "date-fns";
import { getChartGradientColor } from "./colorUtils";
import departments from "../constants/departments";
import overviews from "../constants/overviews";
import { default_donut_data, default_linechart_data } from "../constants/charts";

export const getDonutChartData = (type, data) => {
  let labels = [];
  switch (type) {
    case "department": {
      labels = Object.keys(departments);
      const department_calls_count = {};
      data.forEach((item) => {
        if (department_calls_count[item.department]) {
          department_calls_count[item.department] += 1;
        } else {
          department_calls_count[item.department] = 1;
        }
      });
      const backgroundColor = Object.values(departments).map((value) => value.backgroundColor);
      return {
        ...default_donut_data,
        labels,
        datasets: [
          {
            ...default_donut_data.datasets[0],
            data: labels.map((label) => department_calls_count[label]),
            backgroundColor,
            borderCapStyle: "round",
          },
        ],
      };
    }
    default:
      return default_donut_data;
  }
};

const averageByWeekOrMonth = (data, type) => {
  const result = {};
  if (type === "day") {
    return data;
  }
  Object.keys(data).forEach((date) => {
    const value = data[date];
    const parsedDate = new Date(date);
    let key;
    if (type === "week") {
      const weekNumber = Math.ceil(
        (parsedDate - new Date(parsedDate.getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000)
      );
      key = `Week ${weekNumber}`;
    } else if (type === "month") {
      const monthName = parsedDate.toLocaleString("fr-FR", { month: "long" });
      key = monthName;
    } else {
      throw new Error('Invalid type. Use "week" or "month".');
    }

    if (!result[key]) {
      result[key] = { sum: 0, count: 0 };
    }

    result[key].sum += value;
    result[key].count++;
  });

  // Calculate average for each key
  Object.keys(result).forEach((key) => {
    result[key] = result[key].sum / result[key].count;
  });

  return result;
};

const sumByWeekOrMonth = (data, type) => {
  const result = {};
  if (type === "day") {
    return data;
  }
  Object.keys(data).forEach((date) => {
    const value = data[date];
    const parsedDate = new Date(date);
    let key;

    if (type === "week") {
      const weekNumber = Math.ceil(
        (parsedDate - new Date(parsedDate.getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000)
      );
      key = `Week ${weekNumber}`;
    } else if (type === "month") {
      const monthName = parsedDate.toLocaleString("fr-FR", { month: "long" });
      key = monthName;
    } else {
      throw new Error('Invalid type. Use "week" or "month".');
    }

    if (!result[key]) {
      result[key] = 0;
    }

    result[key] += value;
  });
  return result;
};

export const getLineChartData = (type, data, limit) => {
  try {
    let labels = [];
    const keys = Object.keys(data);
    const start_date = format(new Date(keys[0]), "yyyy-MM-dd");
    const end_date = format(new Date(keys[keys.length - 1]), "yyyy-MM-dd");

    const date_range = (start_date, end_date) => {
      const date1 = new Date(start_date);
      const date2 = new Date(end_date);
      const diffTime = Math.abs(date2.getTime() - date1.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };

    // give me all the dates between two dates
    const getDaysBetweenDates = (start_date, end_date) => {
      const daysList = eachDayOfInterval({
        start: new Date(start_date),
        end: new Date(end_date),
      });
      const days = daysList.map((day) => format(day, "MM/dd"));
      return days;
    };

    const getWeeksBetweenDates = (start_date, end_date) => {
      const weeks = eachWeekOfInterval({
        start: new Date(start_date),
        end: new Date(end_date),
      });
      const weekNumbers = weeks.map((week) => `Week ${getISOWeek(week)}`);
      return weekNumbers;
    };

    const day_count = date_range(start_date, end_date);
    // const days = getDays(start_date, end_date);
    const days = getDaysBetweenDates(start_date, end_date);
    const monthRange = eachMonthOfInterval({
      start: new Date(start_date),
      end: new Date(end_date),
    });

    const weeks = getWeeksBetweenDates(start_date, end_date);
    const months = monthRange.map((month) => format(month, "MMMM"));
    // const selected_labels = day_count <= 15 ? days : day_count < 90 ? weeks : months;

    let grade_limit = null;
    let sum_analytics = null;
    let selected_labels = null;
    let selected_values = null;
    switch (type) {
      case "calls":
      case "operator_sales":
        grade_limit = Array(Object.values(data).length).fill(limit);

        sum_analytics =
          day_count <= 15
            ? averageByWeekOrMonth(data, "day")
            : day_count < 90
            ? averageByWeekOrMonth(data, "week")
            : averageByWeekOrMonth(data, "month");
        selected_labels = Object.keys(sum_analytics);
        selected_values = Object.values(sum_analytics).map((number) => number.toFixed(2));
        return {
          ...default_linechart_data,
          labels: selected_labels,
          datasets: [
            {
              ...default_linechart_data.datasets[0],
              data: selected_values,
              borderWidth: 5,
              borderColor: (context) => {
                // const { chart } = context;
                // const { ctx, chartArea } = chart;
                // if (!chartArea) return;
                // return getChartGradientColor(ctx, chartArea);
                return "#4318FF";
              },
              backgroundColor: (context) => {
                // const { chart } = context;
                // const { ctx, chartArea } = chart;
                // if (!chartArea) return;
                // return getChartGradientColor(ctx, chartArea);
                return "#4318FF";
              },
              pointRadius: 1,
              pointBorderColor: "#4318FF",
            },
            // limit > 0 && {
            //   label: "",
            //   fill: false,
            //   backgroundColor: "#FFF",
            //   borderColor: "#F3F4F4",
            //   borderDash: [7, 7], // Dashed line for better visualization
            //   borderWidth: 3,
            //   pointRadius: 4,
            //   data: grade_limit, // Array of the same value (8.5) as the length of your labels array
            // },
          ],
        };
      case "sales_companies": {
        grade_limit = Array(Object.values(data).length).fill(limit);
        sum_analytics =
          day_count <= 15
            ? sumByWeekOrMonth(data, "day")
            : day_count < 90
            ? sumByWeekOrMonth(data, "week")
            : sumByWeekOrMonth(data, "month");
        selected_labels = Object.keys(sum_analytics);
        selected_values = Object.values(sum_analytics).map((number) => Math.ceil(number));
        return {
          ...default_linechart_data,
          // labels: labels.map((label) => format(new Date(label), "dd/MM")),
          labels: selected_labels,
          // labels: Object.keys(data),
          datasets: [
            {
              ...default_linechart_data.datasets[0],
              // data: labels.map((label) => calls_grades[label]),
              // data: Object.values(data),
              data: selected_values,
              borderWidth: 5,
              borderColor: (context) => {
                // const { chart } = context;
                // const { ctx, chartArea } = chart;
                // if (!chartArea) return;
                // return getChartGradientColor(ctx, chartArea);
                return "#4318FF";
              },
              backgroundColor: (context) => {
                // const { chart } = context;
                // const { ctx, chartArea } = chart;
                // if (!chartArea) return;
                // return getChartGradientColor(ctx, chartArea);
                return "#4318FF";
              },
              pointRadius: 1,
              pointBorderColor: "#4318FF",
            },
            limit > 0 && {
              label: "",
              fill: false,
              backgroundColor: "#FFF",
              borderColor: "#F3F4F4",
              borderDash: [7, 7], // Dashed line for better visualization
              borderWidth: 3,
              pointRadius: 4,
              data: grade_limit, // Array of the same value (8.5) as the length of your labels array
            },
          ],
        };
      }
      default:
    }
  } catch (error) {
    console.log({ error });
  }
};

export const getCallsOverviewData = (calls) => {
  if (Object.values(calls).length === 0) return overviews;
  // TODO change to all_calls after modifing API return value
  let operator_overviews = {
    all: {
      ...overviews.all,
      count: calls.total_calls,
    },
    green_calls: { ...overviews.green_calls, count: calls.green_calls },
    red_calls: { ...overviews.red_calls, count: calls.red_calls },
    orange_calls: { ...overviews.orange_calls, count: calls.orange_calls },
    frustrated_clients: {
      ...overviews.frustrated_clients,
      count: calls.frustrated_clients,
    },
    answerphone: { ...overviews.answerphone, count: calls.answerphone },
  };

  // Taux de décrochage : part des appels sortants ayant été décrochés
  // (= non répondeur) sur l'ensemble des appels sortants.
  const outgoingCalls = calls.outgoing_calls || 0;
  const outgoingAnswerphone = calls.outgoing_answerphone || 0;
  if (outgoingCalls > 0) {
    operator_overviews.outbound_pickup_rate = {
      ...overviews.outbound_pickup_rate,
      count: Math.round(((outgoingCalls - outgoingAnswerphone) / outgoingCalls) * 100),
    };
  }

  return operator_overviews;
};
